import { useQuery } from "@tanstack/react-query";
import { useApi } from "../api/ApiProvider";
import { useOrg } from "./OrgContext";
import { Select } from "./ui/Select";

/**
 * El selector de contexto de la cabecera: entidad legal y sede. Es la espina del
 * backoffice —hasta que el usuario elige, las pantallas operativas no tienen
 * sobre qué operar—. El tenant lo fija la configuración; aquí se listan las
 * entidades legales de ese tenant y las sedes de la entidad elegida, con los
 * endpoints de organización.
 */
export function OrgSelector() {
  const api = useApi();
  const { tenantId, legalEntityId, setLegalEntityId, branchId, setBranchId } = useOrg();

  const entidades = useQuery({
    queryKey: ["legal-entities", tenantId],
    queryFn: async () => {
      const { data, error, response } = await api.GET(
        "/api/v1/tenants/{tenantId}/legal-entities",
        { params: { path: { tenantId } } },
      );
      if (error) throw new Error(`No se pudieron cargar las entidades legales (${response.status}).`);
      return data ?? [];
    },
  });

  const sedes = useQuery({
    queryKey: ["branches", tenantId, legalEntityId],
    enabled: legalEntityId !== null,
    queryFn: async () => {
      if (!legalEntityId) return [];
      const { data, error, response } = await api.GET(
        "/api/v1/tenants/{tenantId}/legal-entities/{legalEntityId}/branches",
        { params: { path: { tenantId, legalEntityId } } },
      );
      if (error) throw new Error(`No se pudieron cargar las sedes (${response.status}).`);
      return data ?? [];
    },
  });

  return (
    <div className="flex flex-wrap items-end gap-3">
      <Select
        label="Entidad legal"
        className="min-w-[13rem]"
        value={legalEntityId}
        placeholder={entidades.isLoading ? "Cargando…" : "Elige entidad legal"}
        options={(entidades.data ?? [])
          .filter((e) => e.id)
          .map((e) => ({ value: e.id as string, label: e.tradeName ?? e.legalName ?? (e.id as string) }))}
        onChange={setLegalEntityId}
      />
      <Select
        label="Sede"
        className="min-w-[11rem]"
        value={branchId}
        disabled={legalEntityId === null}
        placeholder={
          legalEntityId === null ? "Elige entidad primero" : sedes.isLoading ? "Cargando…" : "Elige sede"
        }
        options={(sedes.data ?? [])
          .filter((s) => s.id)
          .map((s) => ({
            value: s.id as string,
            label: [s.code, s.name].filter(Boolean).join(" · ") || (s.id as string),
          }))}
        onChange={setBranchId}
      />
    </div>
  );
}
